import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { withDevice, WithDeviceProps } from './hooks/withDevice';
import Login from './pages/Login';
import Home from './pages/Home';
import ProjectDetails from './pages/ProjectDetails';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import Layout from './components/Layout';
import Footer from './components/Footer';

// Import i18n configuration
import './config/i18n';
import { useTranslation } from 'react-i18next';
import { 
  Close as CloseIcon,
  Menu as MenuIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CameraAlt as CameraIcon,
 } from '@mui/icons-material';
import { ErrorBoundary, LoadingScreen } from './components';
// import Calibration from './pages/Calibration';
import Lanzamiento from './pages/Lanzamiento';
import { getDeviceInfoForBackend } from './utils/deviceInfo';

// Wrapper component to provide auth context to LanguageProvider
const LanguageProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userData } = useAuth();
  
  return (
    <LanguageProvider user={user} userData={userData}>
      {children}
    </LanguageProvider>
  );
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userData, logout } = useAuth();

  if (!user || !userData?.access) {
    logout();
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};


// Generate camera_id from device info using Web Crypto API
const generateCameraId = async (): Promise<string> => {
  const deviceInfo = getDeviceInfoForBackend();
  const deviceString = `${deviceInfo.agent}_${deviceInfo.brand}_${deviceInfo.model}_${deviceInfo.platform}_${deviceInfo.screenWidth}_${deviceInfo.screenHeight}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(deviceString);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex.substring(0, 32);
};

// type CalibrationStatus = 'not_exists' | 'pending' | 'completed' | null;

const AppContent: React.FC<WithDeviceProps> = ({ isMobileDevice }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // const [calibrationStatus, setCalibrationStatus] = useState<CalibrationStatus>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, error, clearError, user } = useAuth();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const menuButton = (
    <IconButton
      edge="start"
      color="inherit"
      aria-label={t('accessibility.menu')}
      onClick={handleDrawerToggle}
      sx={{ mr: 2 }}
    >
      {drawerOpen ? <CloseIcon /> : <MenuIcon />}
    </IconButton>
  );

  // Check calibration status when user is logged in
  // useEffect(() => {
  //   const checkCalibrationStatus = async () => {
  //     if (!user) {
  //       setCalibrationStatus(null);
  //       return;
  //     }

  //     try {
  //       const idToken = await user.getIdToken();
  //       const cameraId = await generateCameraId();
        
  //       const response = await axios.get(
  //         `${process.env.REACT_APP_API_URL || ''}/api/calibration/${cameraId}`,
  //         {
  //           headers: { 'Authorization': `Bearer ${idToken}` },
  //         }
  //       );

  //       if (response.data?.calibration) {
  //         const calibration = response.data.calibration;
  //         const status = calibration.status;
          
  //         if (status === 'pending') {
  //           setCalibrationStatus('pending');
  //         } else if (status === 'completed') {
  //           setCalibrationStatus('completed');
  //         } else {
  //           setCalibrationStatus('pending'); // Default to pending for other statuses
  //         }
  //       } else {
  //         setCalibrationStatus('not_exists');
  //       }
  //     } catch (err) {
  //       // Calibration doesn't exist
  //       if (axios.isAxiosError(err) && err.response?.status === 404) {
  //         setCalibrationStatus('not_exists');
  //       } else if (axios.isAxiosError(err)) {
  //         console.error('Error checking calibration status:', err);
  //         setCalibrationStatus(null); // Don't show icon on error
  //       } else {
  //         setCalibrationStatus('not_exists');
  //       }
  //     }
  //   };

  //   if (user && !loading && isMobileDevice) {
  //     checkCalibrationStatus();
  //   } else {
  //     setCalibrationStatus(null);
  //   }
  // }, [user, loading]);

  // Show loading screen while auth is loading
  if (loading) {
    return <LoadingScreen message="" />;
  }

  // Show error screen if there's an auth error
  if (error) {
    return (
      <LoadingScreen 
        error={error} 
        onRetry={clearError}
      />
    );
  }

  // Hide AppBar on login page
  const showAppBar = location.pathname !== '/login';
  const showFooter = location.pathname !== '/login';
  return (
    <Box sx={{ margin: 0, padding: 0 }}>
      {showAppBar && (
        <AppBar 
          position="fixed"
          sx={{ 
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary'}}
        >
          <Toolbar>
            <img src="/icon.jpeg" alt="Logo" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>PDI Futuro</Typography>
            {/* {isMobileDevice && calibrationStatus === 'not_exists' && (
              <Tooltip title={t('settings.calibration.notExists') || 'Camera calibration does not exist'}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <IconButton 
                    onClick={() => {
                      navigate('/settings');
                    }}
                    size="small"
                  >
                    <CameraIcon
                      sx={{ 
                        height: 26, 
                        width: 26,
                      }} 
                    />
                  </IconButton>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -2.1,
                    right: -2.5,
                    backgroundColor: 'transparent',
                    borderRadius: '50%',
                    padding: 0.1,
                    color: 'error.main'
                  }}>
                    <ErrorIcon sx={{ height: 16, width: 16 }} />
                  </Box>
                </Box>
              </Tooltip>
            )}
            {isMobileDevice && calibrationStatus === 'pending' && (
              <Tooltip title={t('settings.calibration.pending') || 'Camera calibration is pending'}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <IconButton 
                    onClick={() => {
                      navigate('/settings');
                    }}
                    size="small"
                  >
                    <CameraIcon
                      sx={{ 
                        height: 26, 
                        width: 26,
                      }} 
                    />
                  </IconButton>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -2.1,
                    right: -2.5,
                    backgroundColor: 'transparent',
                    borderRadius: '50%',
                    padding: 0.1,
                    color: 'warning.main'
                  }}>
                    <WarningIcon sx={{ height: 16, width: 16 }} />
                  </Box>
                </Box>
              </Tooltip>
            )} */}
          </Toolbar>
        </AppBar>
      )}
        <LanguageProviderWrapper>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <Layout drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen}>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <PrivateRoute><Home /></PrivateRoute>
                      }
                    />
                    {/* <Route
                      path="/calibration"
                      element={
                        <PrivateRoute>
                          <Calibration/>
                        </PrivateRoute>
                      }
                    /> */}
                    <Route
                      path="/project/:projectCode"
                      element={
                        <PrivateRoute><ProjectDetails isMobileDevice={isMobileDevice} /></PrivateRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                          <PrivateRoute><Settings isMobileDevice={isMobileDevice} /></PrivateRoute> 
                      }
                    />
                    <Route
                      path="/lanzamiento"
                      element={
                        <PrivateRoute>
                          {!isMobileDevice ? (
                            <Lanzamiento />
                          ) : (
                            <NotFound />
                          )}
                        </PrivateRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
        </LanguageProviderWrapper>
      {showFooter && (
        <Footer />
      )}
    </Box>
  );
};

const App: React.FC<WithDeviceProps> = ({ isMobileDevice }) => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AppContent isMobileDevice={isMobileDevice} />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default withDevice(App);
